import { HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID, REQUEST } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { STRAPI_CONFIG } from './strapi.config';
import { PREVIEW_VERIFIER } from './preview-verifier';
import {
  PREVIEW_COOKIE_NAME,
  readPreviewCookie,
} from '../../../preview-cookie-shared';

/**
 * Strapi HTTP interceptor — attaches auth token and draft-mode param.
 *
 * Behaviour matrix:
 *
 * | Context                                    | Action                                         |
 * |--------------------------------------------|------------------------------------------------|
 * | Non-Strapi URL                             | Pass-through, no modifications                 |
 * | Browser (isPlatformServer=false)           | Pass-through — token is server-only by rule    |
 * | Server, no preview cookie                  | Attach `Authorization: Bearer <apiToken>`      |
 * | Server, valid preview cookie               | Attach `Authorization: Bearer <apiToken>`      |
 * |                                            |   + append/replace `status=draft` query param  |
 * | Server, invalid/expired cookie             | Same as "no preview cookie" — fail closed      |
 * | Server, PREVIEW_VERIFIER not provided      | Same as "no preview cookie" — fail closed      |
 *
 * Strapi v5 uses the same read-only token for published and draft content
 * — draft access is gated at the URL via ?status=draft, not at the token.
 * So we only swap the URL on draft mode, not the bearer token.
 *
 * The preview cookie is HMAC-verified (see src/preview-cookie-server.ts)
 * via a server-only provider bound through the PREVIEW_VERIFIER token.
 * A browser cannot forge a draft-mode session without PREVIEW_SECRET,
 * and an expired cookie fails verification so stale sessions quietly
 * drop back to public read automatically.
 *
 * PREV-04: because every SSR render re-reads the incoming Fetch Request's
 * cookie header, draft mode persists across navigations automatically
 * until the browser's cookie expires or `/api/exit-preview` clears it.
 * No per-request re-authentication is involved — re-verification is
 * signature validation, not user re-prompting.
 *
 * SECURITY INVARIANTS (enforced by grep rules in verification):
 *   - This file does NOT import from `./preview-cookie-server` or
 *     `../../../preview-cookie-server`. All HMAC verification is done
 *     behind the PREVIEW_VERIFIER injection token.
 *   - This file does NOT reference the Node env directly. The secret
 *     flows through STRAPI_CONFIG (which is the one authorised env
 *     reader — see strapi.config.ts).
 *   - This file does NOT import `node:crypto`, `createHmac`, or
 *     `timingSafeEqual`. Those live only in preview-cookie-server.ts.
 */
export const strapiAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const config = inject(STRAPI_CONFIG);
  const platformId = inject(PLATFORM_ID);

  // Only intercept requests to the Strapi base URL. All preview logic
  // below is gated on this check too — we never rewrite non-Strapi URLs.
  if (!req.url.startsWith(config.baseUrl)) {
    return next(req);
  }

  // Preview / auth logic is server-only. PROJECT.md "Server-only auth
  // token" decision: token MUST NEVER be attached in the browser.
  if (!isPlatformServer(platformId)) {
    return next(req);
  }

  // Server-only verifier. On the browser this resolves to null because
  // only app.config.server.ts provides PREVIEW_VERIFIER. If it's null
  // on the server (e.g., misconfigured provider), we fail closed to
  // legacy behaviour — never throw, never assume draft.
  const verifier = inject(PREVIEW_VERIFIER, { optional: true });

  // Read the incoming SSR request's cookie header via Angular 21's
  // REQUEST token (InjectionToken<Request | null>, where Request is the
  // Fetch API Request). Under unit tests / non-SSR server contexts this
  // will be null, in which case we fall back to no-cookie behaviour —
  // never throw.
  const serverReq = inject(REQUEST, { optional: true });
  const cookieHeader = serverReq?.headers?.get?.('cookie') ?? undefined;

  const rawCookie = readPreviewCookie(cookieHeader);
  const isDraftMode = !!verifier && verifier.verify(rawCookie);

  const outgoing = isDraftMode ? req.clone({ url: withDraftStatus(req.url) }) : req;

  if (config.apiToken) {
    return next(outgoing.clone({
      setHeaders: { Authorization: `Bearer ${config.apiToken}` },
    }));
  }

  return next(outgoing);
};

/**
 * Return `url` with any `status=<anything>` replaced by `status=draft`,
 * or `status=draft` appended when no status param exists. Handles the
 * three cases:
 *   1. No query string at all           → `?status=draft`
 *   2. Existing query string, no status → `&status=draft`
 *   3. Existing status=...              → replace value
 * Exported for direct unit testing in the spec file.
 */
export function withDraftStatus(url: string): string {
  const [base, query = ''] = url.split('?');
  // Case 1: no existing query
  if (!query) return `${base}?status=draft`;
  // Case 3: existing status param — replace the value
  if (/(^|&)status=/.test(query)) {
    const rewritten = query.replace(/(^|&)status=[^&]*/, '$1status=draft');
    return `${base}?${rewritten}`;
  }
  // Case 2: query exists but no status — append
  return `${base}?${query}&status=draft`;
}

// Re-export cookie name for consumers (e.g. tests or future preview-
// aware client-side UI indicator). Imported from the SHARED (pure) file.
export { PREVIEW_COOKIE_NAME };
